import axios from "axios";

// Helper: read a cookie by name
function getCookie(name) {
  try {
    const match = document.cookie.match(
      new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)")
    );
    if (!match) return null;
    let val = match[1];
    if (val && val.includes("%")) {
      try {
        val = decodeURIComponent(val);
      } catch (e) {}
    }
    return val ? val.trim() : null;
  } catch (e) {
    return null;
  }
}

// Helper: set a cookie with path, root domain, and lifetime
function setCookie(name, value, days = 90) {
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    let domainStr = "";
    try {
      const host = window.location.hostname;
      if (host && host !== "localhost" && !/^\d+\.\d+\.\d+\.\d+$/.test(host)) {
        const parts = host.split(".");
        // Kuwait domain (.org.kw) or standard (.com) root domain
        const root = parts.length >= 3 ? parts.slice(-3).join(".") : parts.join(".");
        domainStr = `;domain=.${root}`;
      }
    } catch (e) {}
    document.cookie = `${name}=${value};expires=${expires};path=/${domainStr};SameSite=Lax`;
  } catch (e) {}
}

/**
 * Validate a raw fbclid string:
 * Case-sensitive, alphanumeric + underscore + hyphen, min length 15.
 * Never "undefined", "null", or containing spaces.
 */
export function isValidFbclid(val) {
  if (!val || typeof val !== "string") return false;
  const trimmed = val.trim();
  if (trimmed.length < 15) return false;
  const lower = trimmed.toLowerCase();
  if (lower === "undefined" || lower === "null" || lower === "none" || lower === "false") return false;
  return /^[A-Za-z0-9_-]+$/.test(trimmed);
}

/**
 * Validate an fbc string:
 * Format: fb.{subdomainIndex}.{creationTimeInMs}.{fbclid}
 * Must not be older than 90 days.
 */
export function isValidFbc(fbc) {
  if (!fbc || typeof fbc !== "string") return false;
  const parts = fbc.trim().split(".");
  if (parts.length !== 4) return false;
  if (parts[0] !== "fb") return false;
  if (!/^\d+$/.test(parts[1])) return false;
  if (!/^\d+$/.test(parts[2])) return false;
  const ts = Number(parts[2]);
  if (!ts || isNaN(ts)) return false;
  const age = Date.now() - ts;
  if (age < 0 || age > 90 * 864e5) return false;
  return isValidFbclid(parts[3]);
}

/**
 * Extract raw fbclid directly from the URL query or hash.
 * Does NOT use URLSearchParams to prevent replacing '+' with spaces.
 */
function getRawFbclidFromUrl() {
  try {
    if (typeof window === "undefined" || !window.location) return null;
    const search = window.location.search || "";
    const hash = window.location.hash || "";
    const combined = search + (hash.includes("?") ? hash.substring(hash.indexOf("?")) : "");
    const match = combined.match(/[?&]fbclid=([^&#]+)/);
    if (match && match[1]) {
      let val = match[1];
      if (val.includes("%")) {
        try {
          val = decodeURIComponent(val);
        } catch (e) {}
      }
      val = val.trim();
      if (isValidFbclid(val)) {
        return val;
      }
    }
  } catch (e) {}
  return null;
}

// Helper: extract a generic query parameter from URL (supporting search and hash)
function getUrlParam(param) {
  try {
    if (typeof window !== "undefined" && window.location) {
      if (window.location.search) {
        const val = new URLSearchParams(window.location.search).get(param);
        if (val) return val;
      }
      if (window.location.hash && window.location.hash.includes("?")) {
        const hashQuery = window.location.hash.substring(window.location.hash.indexOf("?"));
        const val = new URLSearchParams(hashQuery).get(param);
        if (val) return val;
      }
    }
  } catch (e) {}
  return null;
}

/**
 * Initialize / resolve Click ID (fbc):
 * Format: fb.{subdomainIndex}.{creationTimeInMs}.{fbclid}
 *
 * Rules:
 * 1. If Meta's official Pixel has already set an _fbc cookie, PRESERVE IT VERBATIM.
 *    Never overwrite it or generate a new timestamp, which causes pixel-server mismatch!
 * 2. If no cookie exists, but user landed with a fresh fbclid query param, format and store it.
 * 3. If a valid, non-expired _fbc exists in localStorage, reuse it.
 * 4. Never send fbc for organic/direct sessions without a valid ad click.
 */
export function resolveFbc() {
  // 1. FIRST: Check if Meta's Pixel (fbevents.js) has already set the _fbc cookie
  const cookieFbc = getCookie("_fbc");
  if (cookieFbc && isValidFbc(cookieFbc)) {
    try {
      localStorage.setItem("_fbc", cookieFbc);
      const parts = cookieFbc.split(".");
      if (parts[3] && isValidFbclid(parts[3])) {
        localStorage.setItem("fbclid", parts[3]);
      }
    } catch (e) {}
    return cookieFbc;
  }

  // 2. If no cookie, check URL for fresh incoming fbclid
  const urlFbclid = getRawFbclidFromUrl();
  if (urlFbclid) {
    const generatedFbc = `fb.1.${Date.now()}.${urlFbclid}`;
    setCookie("_fbc", generatedFbc, 90);
    try {
      localStorage.setItem("fbclid", urlFbclid);
      localStorage.setItem("_fbc", generatedFbc);
    } catch (e) {}
    return generatedFbc;
  }

  // 3. Fallback: check localStorage for previously saved valid _fbc (< 90 days old)
  try {
    const localFbc = localStorage.getItem("_fbc");
    if (localFbc && isValidFbc(localFbc)) {
      setCookie("_fbc", localFbc, 90);
      return localFbc;
    }
  } catch (e) {}

  // 4. Do not fabricate an fbc value or mint a new timestamp if no valid ad click exists!
  return null;
}

export function initFbcTracking() {
  return resolveFbc();
}

/**
 * Resolve / generate Browser ID (fbp):
 * Format: fb.1.{creationTimeInMs}.{10DigitRandom}
 */
export function resolveFbp() {
  let fbp = getCookie("_fbp");
  if (!fbp) {
    try {
      fbp = localStorage.getItem("_fbp");
    } catch (e) {}
  }

  // If pixel has not set _fbp yet or blocked, generate a valid compliant one
  if (!fbp) {
    const rand = Math.floor(1000000000 + Math.random() * 9000000000);
    fbp = `fb.1.${Date.now()}.${rand}`;
    setCookie("_fbp", fbp, 90);
  }

  try {
    localStorage.setItem("_fbp", fbp);
  } catch (e) {}

  return fbp;
}

/**
 * Resolve / generate External ID (external_id) for Meta CAPI event matching:
 * Matches logged-in user ID, or creates a persistent unique visitor ID.
 */
export function resolveExternalId(extraData = {}) {
  let extId = extraData.external_id || null;

  if (!extId) {
    try {
      const userId = localStorage.getItem("user_id");
      if (userId) {
        extId = `user_${userId}`;
      }
    } catch (e) {}
  }

  if (!extId) {
    try {
      extId = localStorage.getItem("external_id") || getCookie("_ext_id");
    } catch (e) {}
  }

  if (!extId) {
    extId = "ext_" + Date.now().toString(36) + "_" + Math.random().toString(36).substring(2, 9);
    try {
      localStorage.setItem("external_id", extId);
      setCookie("_ext_id", extId, 365);
    } catch (e) {}
  }

  return extId;
}

function resolveMobile(extraData = {}) {
  let mobile = extraData.mobile || extraData.phone || null;

  if (mobile) {
    try {
      localStorage.setItem("donor_mobile", String(mobile).trim());
    } catch (e) {}
  } else {
    try {
      mobile = localStorage.getItem("donor_mobile") || localStorage.getItem("mobile");
    } catch (e) {}
  }

  return mobile ? String(mobile).trim() : null;
}

function resolveCcode(extraData = {}) {
  let ccode = extraData.ccode || null;
  if (ccode) {
    try {
      localStorage.setItem("donor_ccode", String(ccode).trim());
    } catch (e) {}
  } else {
    try {
      ccode = localStorage.getItem("donor_ccode") || localStorage.getItem("ccode") || "+965";
    } catch (e) {}
  }
  return ccode || "+965";
}

export function resolveEmail(extraData = {}) {
  let email = extraData.email || extraData.email_address || extraData.donor_email || null;
  if (email && email !== "root" && String(email).includes("@")) {
    try {
      localStorage.setItem("donor_email", String(email).trim());
    } catch (e) {}
  } else {
    try {
      email = localStorage.getItem("donor_email") || localStorage.getItem("email");
      if (email === "root" || !String(email).includes("@")) email = null;
    } catch (e) {}
  }
  return email ? String(email).trim() : null;
}

export function resolveName(extraData = {}) {
  let name = extraData.name || null;
  if (name) {
    try {
      localStorage.setItem("donor_name", String(name).trim());
    } catch (e) {}
  } else {
    try {
      name = localStorage.getItem("donor_name") ||
        [localStorage.getItem("first_name"), localStorage.getItem("last_name")].filter(Boolean).join(" ");
    } catch (e) {}
  }
  return name && String(name).trim() ? String(name).trim() : null;
}

export function resolveFirstAndLastName(extraData = {}, resolvedName = null) {
  let fn = extraData.first_name || null;
  let ln = extraData.last_name || null;

  if (!fn || !ln) {
    try {
      if (!fn) fn = localStorage.getItem("first_name");
      if (!ln) ln = localStorage.getItem("last_name");
    } catch (e) {}
  }

  if (!fn || !ln) {
    const fullName = resolvedName || resolveName(extraData);
    if (fullName) {
      const parts = String(fullName).trim().split(/\s+/);
      if (!fn && parts.length > 0) fn = parts[0];
      if (!ln && parts.length > 1) ln = parts.slice(1).join(" ");
    }
  }

  if (fn) {
    try {
      localStorage.setItem("first_name", String(fn).trim());
    } catch (e) {}
  }
  if (ln) {
    try {
      localStorage.setItem("last_name", String(ln).trim());
    } catch (e) {}
  }

  return {
    first_name: fn ? String(fn).trim() : null,
    last_name: ln ? String(ln).trim() : null,
  };
}

export function resolveDonorData(extraData = {}) {
  const rawFbc = resolveFbc();
  const fbc = rawFbc && isValidFbc(rawFbc) ? rawFbc : null;
  const fbp = resolveFbp();
  const external_id = resolveExternalId(extraData);
  const mobile = resolveMobile(extraData);
  const ccode = resolveCcode(extraData);
  const email = resolveEmail(extraData);
  const name = resolveName(extraData);
  const { first_name, last_name } = resolveFirstAndLastName(extraData, name);

  return {
    fbc,
    fbp,
    external_id,
    mobile,
    ccode,
    email,
    name,
    first_name,
    last_name,
    country: "kw",
  };
}

// Auto-run once on script load to catch any fbclid in URL
initFbcTracking();

// Fire-and-forget: tracking must never block or break the donation flow
export default function sendEvent(title, balance = 0, currency = "KWD", extra = {}) {
  const extraData = typeof extra === "object" && extra !== null ? extra : { mobile: extra };

  // sendEvent lives on the API root, not under /v1
  const url = String(window.baseURL).replace(/\/v1\/?$/, "") + "/sendEvent";

  // 0. Event ID for deduplication between Pixel and CAPI
  const eventId =
    extraData.event_id ||
    `evt_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

  const payload = {
    title: title,
    link: window.location.href,
    value: Number(balance) || 0,
    balance: Number(balance) || 0,
    currency: currency || "KWD",
    event_id: eventId,
  };

  // 1. Click ID (fbc) & raw fbclid
  const fbc = resolveFbc();
  if (fbc && isValidFbc(fbc)) {
    payload.fbc = fbc;
    const parts = fbc.split(".");
    if (parts[3] && isValidFbclid(parts[3])) {
      payload.fbclid = parts[3];
    }
  }

  // 2. Browser ID (fbp)
  const fbp = resolveFbp();
  if (fbp) payload.fbp = fbp;

  // 3. External ID (external_id)
  const externalId = resolveExternalId(extraData);
  if (externalId) payload.external_id = externalId;

  // 4. Mobile / Phone number
  const mobile = resolveMobile(extraData);
  if (mobile) payload.mobile = mobile;

  // 5. Country code
  const ccode = resolveCcode(extraData);
  if (ccode) payload.ccode = ccode;

  // 6. Email
  const email = resolveEmail(extraData);
  if (email) payload.email = email;

  // 7. Name & First/Last Name
  const name = resolveName(extraData);
  if (name) payload.name = name;

  const { first_name, last_name } = resolveFirstAndLastName(extraData, name);
  if (first_name) payload.first_name = first_name;
  if (last_name) payload.last_name = last_name;

  // 8. Country (Kuwait default / GCC)
  payload.country = "kw";

  // Attach auth header if user is logged in
  const headers = {};
  try {
    const token = localStorage.getItem("access_token");
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  } catch (e) {}

  axios.post(url, payload, { headers }).catch(() => {});
  return eventId;
}
